import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COLLECTION = 'profileComments';
const WALL_PAGE_SIZE = 50;

// Listens to a profile's comments. The visibility filter depends on the viewer:
//   - Mods (canModerate=true): every comment regardless of status.
//   - Logged-in non-mods: visible comments + any hidden ones the viewer themself reported.
//   - Anonymous: only visible comments.
// Two-listener merge for non-mods because Firestore queries can't OR across statuses.
export const listenToProfileWall = (ownerUserId, callback, options = {}) => {
  const { canModerate = false, currentUid = null, onError } = options;
  if (!ownerUserId) {
    callback([]);
    return () => {};
  }

  const handleErr = (err) => {
    if (onError) onError(err);
    else console.error('listenToProfileWall:', err);
  };

  if (canModerate) {
    const q = query(
      collection(db, COLLECTION),
      where('ownerUserId', '==', ownerUserId),
      orderBy('createdAt', 'desc'),
      limit(WALL_PAGE_SIZE)
    );
    return onSnapshot(
      q,
      (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      handleErr
    );
  }

  let visibleRows = [];
  let reportedRows = [];
  const emit = () => {
    const merged = [...visibleRows, ...reportedRows];
    merged.sort((a, b) => {
      const am = a.createdAt?.toMillis?.() ?? 0;
      const bm = b.createdAt?.toMillis?.() ?? 0;
      return bm - am;
    });
    callback(merged);
  };

  const visibleQ = query(
    collection(db, COLLECTION),
    where('ownerUserId', '==', ownerUserId),
    where('status', '==', 'visible'),
    orderBy('createdAt', 'desc'),
    limit(WALL_PAGE_SIZE)
  );
  const unsubVisible = onSnapshot(
    visibleQ,
    (snap) => {
      visibleRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      emit();
    },
    handleErr
  );

  let unsubReported = () => {};
  if (currentUid) {
    const reporterQ = query(
      collection(db, COLLECTION),
      where('ownerUserId', '==', ownerUserId),
      where('status', '==', 'hidden'),
      where('reportedBy', 'array-contains', currentUid),
      orderBy('createdAt', 'desc'),
      limit(WALL_PAGE_SIZE)
    );
    unsubReported = onSnapshot(
      reporterQ,
      (snap) => {
        reportedRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        emit();
      },
      handleErr
    );
  }

  return () => {
    unsubVisible();
    unsubReported();
  };
};

export const addComment = async ({ ownerUserId, author, text }) => {
  if (!ownerUserId) throw new Error('ownerUserId is required');
  if (!author?.uid) throw new Error('author.uid is required');
  const trimmed = (text || '').trim();
  if (!trimmed) throw new Error('Comment cannot be empty.');
  if (trimmed.length > 1000) throw new Error('Comment is too long (max 1000 characters).');

  const ref = await addDoc(collection(db, COLLECTION), {
    ownerUserId,
    authorUserId: author.uid,
    authorUsername: author.username || 'Unknown',
    authorPhotoURL: author.photoURL || null,
    text: trimmed,
    createdAt: serverTimestamp(),
    reportCount: 0,
    reportedBy: [],
    status: 'visible',
    removedBy: null,
    removedAt: null,
  });
  return ref.id;
};

export const deleteComment = (commentId) => deleteDoc(doc(db, COLLECTION, commentId));

export const reportComment = (commentId, uid) => {
  if (!commentId || !uid) throw new Error('commentId and uid are required');
  return updateDoc(doc(db, COLLECTION, commentId), {
    status: 'hidden',
    reportCount: increment(1),
    reportedBy: arrayUnion(uid),
  });
};

export const listenToModerationQueue = (callback, onError) => {
  const q = query(
    collection(db, COLLECTION),
    where('status', '==', 'hidden'),
    orderBy('reportCount', 'desc'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      if (onError) onError(err);
      else console.error('listenToModerationQueue:', err);
    }
  );
};

export const restoreComment = (commentId) =>
  updateDoc(doc(db, COLLECTION, commentId), {
    status: 'visible',
    reportCount: 0,
    reportedBy: [],
  });

export const removeComment = (commentId, modUid) =>
  updateDoc(doc(db, COLLECTION, commentId), {
    status: 'removed',
    removedBy: modUid || null,
    removedAt: serverTimestamp(),
  });
